import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {WorklistItemsQuery} from 'worklists/__generated/WorklistItemsQuery';
import {ListItemValidation, SelectItemsAdditionalColumn} from 'worklists/SelectItemsFromListView';
import {SelectItemsFromWorklistView, WorklistItemNode} from 'worklists/SelectItemsFromWorklistView';
import {WorklistSummary} from 'worklists/WorklistConstants';

export interface SelectItemsFromWorklistModalResult<TWorklistItemNode extends WorklistItemNode> {
  worklistSummary: WorklistSummary;
  selectedWorklistItems: Array<ExcludeInaccessible<TWorklistItemNode>>;
}

export interface SelectItemsFromWorklistModalProps<TWorklistItemNode extends WorklistItemNode> {
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectItemsFromWorklistModalResult<TWorklistItemNode>) => void;
  validateItem: (worklistItemNode: TWorklistItemNode) => ListItemValidation;
  query?: DocumentNode | null;
  additionalColumns?: Array<SelectItemsAdditionalColumn<TWorklistItemNode>>;
}

/** A modal for selecting items from a worklist. Allows selecting a subset of items.
 *
 * @param onRequestClose Callback executed when the modal is closed.
 * @param onRequestSubmit Callback executed with the selected item api ids when flow is complete.
 * @param validateItem Function for validating worklist items to determine if they can be selected.
 * @param query Optional GQL query to fetch worklist items. If none is passed, it will use the query in
 *              SelectItemsFromWorklistView
 * @constructor
 */
export function SelectItemsFromWorklistModal<TWorklistItemsQuery extends WorklistItemsQuery>({
  onRequestClose,
  onRequestSubmit,
  validateItem,
  query,
  additionalColumns,
}: SelectItemsFromWorklistModalProps<
  ExtractByTypename<TWorklistItemsQuery['worklist'], 'WorklistNode'>['items'][number]
>): JSX.Element {
  type TWorklistItemNode = ExtractByTypename<
    TWorklistItemsQuery['worklist'],
    'WorklistNode'
  >['items'][number];
  const [worklistSummary, setWorklistSummary] = useState<WorklistSummary | null>(null);
  const [selectedWorklistItems, setSelectedWorklistItems] = useState<Array<TWorklistItemNode>>([]);

  const buttons = [
    {text: 'Cancel', cancel: true},
    {
      text: `Add ${selectedWorklistItems.length} ${pluralize(selectedWorklistItems.length, 'item')}`,
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: selectedWorklistItems.length === 0,
      dataTestAttrs: {component: 'AddFromWorklistModal', key: ModalButtonKey.NEXT},
    },
  ];

  return (
    <Modal
      title="Add from worklist"
      onRequestClose={onRequestClose}
      buttons={buttons}
      onRequestSubmit={() => {
        onRequestSubmit({
          worklistSummary: worklistSummary!,
          // Inaccessible items cannot be selected
          selectedWorklistItems: selectedWorklistItems as Array<ExcludeInaccessible<TWorklistItemNode>>,
        });
      }}
      dataTestAttrs={{component: 'SelectItemsFromWorklistModal', element: 'root'}}
      expandHeight={true}
    >
      <SelectItemsFromWorklistView<TWorklistItemsQuery>
        selectedWorklistItems={selectedWorklistItems}
        setSelectedWorklistItems={setSelectedWorklistItems}
        setWorklistSummary={setWorklistSummary}
        validateItem={validateItem}
        worklistSummary={worklistSummary}
        query={query}
        additionalColumns={additionalColumns}
      />
    </Modal>
  );
}
